import { Action } from 'redux-pack';
import type { SettingsReducerState } from '../stores/types/settings';
import type { ShotsState } from '../stores/types/shots';
import type { VideoState } from '../stores/types/video';
import type { MediaState } from '../stores/types/media';
import type { GraphicsState } from '../stores/types/graphics';
import type { BroadcastingState } from '../stores/types/broadcasting';
import type { MeetingReducerState } from '../stores/types/meeting';

export interface SyncedActionData {
	stateVar: string; // the reducer variable we're mutating with this action
	reducer: string; // the reducer the variable lives in
	conditional: boolean; // if theres any client-side parsing we should do when the state is applied
	// optional, set if the variable needs to be reset once the meeting is empty
	resetOnEmpty?: boolean;
}
export interface SyncedState {
	shotsReducer?: ShotsState;
	settingsReducer?: SettingsReducerState;
	mediaReducer?: MediaState;
	videoReducer?: VideoState;
	graphicsReducer?: GraphicsState;
	broadcastReducer?: BroadcastingState;
	meetingReducer?: MeetingReducerState;
}

export interface GreenroomAction extends Action {
	data?: any;
	fireOnRemote?: boolean;
}

export interface SyncedActionMessage {
	action: GreenroomAction,
	data: SyncedActionData,
	session: string,
}

/*
Make sure to update SyncedState interface (above) when you're adding a
new var here, and use the ? syntax. There's no guarantee that an action will
fire that sets the state, before someone has joined the room, so any variable in our
SyncedState interface could be undefined. Basically, there's no base synced state,
it only gets built from variables when actions fire.

If you are adding state from a reducer that doesn't yet have a SYNC_*reducer*_REDUCER_STATE
action, look in shots reducer for the SYNC_SHOTS_REDUCER_STATE, and copy that action over to
the new reducer. Make sure to also update parseAndApplyStateFromRemote function.

If you need to run logic based on the client app's state when synced state is fetched-
set the conditional flag,
add logic to handleConditionalState in ./sync.ts
add logic to handleConditionalAction function in ./sync.ts
*/
export const syncedReduxActions = new Map<string, SyncedActionData>([
	['SET_CURRENT_SHOT_ID', {
		stateVar: 'currentShotId', reducer: 'shotsReducer', conditional: true, resetOnEmpty: true,
	}],
	['SET_CLEAR_SHOT', {
		stateVar: 'clearShot', reducer: 'settingsReducer', conditional: false, resetOnEmpty: true,
	}],
	['SET_DEFAULT_RESOLUTION', { stateVar: 'defaultResolution', reducer: 'settingsReducer', conditional: false }],
	['SET_PRESENTED_MEDIA', {
		stateVar: 'presentedMedia', reducer: 'mediaReducer', conditional: true, resetOnEmpty: true,
	}],
	['SET_ENABLED_GRAPHICS', {
		stateVar: 'enabledGraphics', reducer: 'graphicsReducer', conditional: false, resetOnEmpty: true,
	}],
	['SET_ANIMATED_GRAPHICS', {
		stateVar: 'animatedGraphics', reducer: 'graphicsReducer', conditional: false, resetOnEmpty: true,
	}],
	['REMOVE_ENABLED_GRAPHIC', {
		stateVar: 'enabledGraphics', reducer: 'graphicsReducer', conditional: false, resetOnEmpty: true,
	}],
	['ADD_ANIMATED_GRAPHIC', {
		stateVar: 'animatedGraphics', reducer: 'graphicsReducer', conditional: false, resetOnEmpty: true,
	}],
	['SET_PRESENTED_MEDIA_PAGE', {
		stateVar: 'presentedMedia', reducer: 'mediaReducer', conditional: false, resetOnEmpty: true,
	}],
	['SET_PRESENTED_MEDIA_VIDEOSTATE', {
		stateVar: 'presentedMedia', reducer: 'mediaReducer', conditional: false, resetOnEmpty: true,
	}],
	['ADD_PRESENTED_MEDIA', {
		stateVar: 'presentedMedia', reducer: 'mediaReducer', conditional: false, resetOnEmpty: true,
	}],
	['REMOVE_PRESENTED_MEDIA', {
		stateVar: 'presentedMedia', reducer: 'mediaReducer', conditional: false, resetOnEmpty: true,
	}],
	['SET_RECORDING_OPTION', {
		stateVar: 'selectedRecordingOption', reducer: 'broadcastReducer', conditional: false, resetOnEmpty: false,
	}],
	['SET_BROADCAST_STARTING_SHOT', {
		stateVar: 'broadcastStartingShotId', reducer: 'broadcastReducer', conditional: false, resetOnEmpty: true,
	}],
	['SET_BROADCAST_STARTED_BY', {
		stateVar: 'broadcastStartedBy', reducer: 'broadcastReducer', conditional: false, resetOnEmpty: true,
	}],
	['SET_CURRENT_RECORDING_STREAM_ID', {
		stateVar: 'currentRecordingStreamId', reducer: 'broadcastReducer', conditional: false, resetOnEmpty: true,
	}],
	['SET_CANVAS_VOLUME_BACKSTAGE', {
		stateVar: 'backstageCanvasVolume', reducer: 'meetingReducer', conditional: false, resetOnEmpty: true,
	}],
	['SET_WARNING_MODAL_SENT', {
		stateVar: 'warningModalSent', reducer: 'sessionReducer', conditional: false, resetOnEmpty: true,
	}],
	['SET_VIDEO_TITLES_HAVE_FIRED', {
		stateVar: 'videoTitlesHaveFired', reducer: 'shotsReducer', conditional: true, resetOnEmpty: true,
	}],
	['SET_PRESENTER_AUDIENCE_TAB', { stateVar: 'presenterAudienceTab', reducer: 'settingsReducer', conditional: false }],
]);
